import { useState, useRef, useEffect, useCallback, type ChangeEvent as ReactChangeEvent } from 'react'
import { SearchIcon, LoaderIcon } from '../icons'
import type { GiphyGif } from '../types'

// Giphy API key — set VITE_GIPHY_API_KEY in .env for production.
// Falls back to Giphy's public beta key so the picker works out of the box.
const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || 'Gc7131jiJuvI7IdN0HZ1D7nh0ow5BU6g'
const API_URL = 'https://api.giphy.com/v1/gifs'
const LIMIT = 24

/**
 * GIF picker powered by Giphy.
 *
 * Props:
 *   onSelect(file) — called with a File object (image/gif) ready to send as attachment
 *   onClose()      — called when the picker should close
 */
export function GifPicker({ onSelect, onClose }: { onSelect: (file: File) => void; onClose: () => void }) {
  const [query, setQuery] = useState('')
  const [gifs, setGifs] = useState<GiphyGif[]>([])
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState<string | null>(null) // gif id being downloaded
  const [error, setError] = useState('')
  const reqIdRef = useRef(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose?.()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const loadTrending = useCallback(async () => {
    setLoading(true)
    setError('')
    const id = ++reqIdRef.current
    try {
      const res = await fetch(`${API_URL}/trending?api_key=${GIPHY_API_KEY}&limit=${LIMIT}&rating=pg`)
      if (!res.ok) throw new Error('Giphy error ' + res.status)
      const data = await res.json()
      if (id === reqIdRef.current) setGifs(data.data || [])
    } catch (e: unknown) {
      if (id === reqIdRef.current) setError('Falha ao carregar GIFs')
    } finally {
      if (id === reqIdRef.current) setLoading(false)
    }
  }, [])

  const searchGifs = useCallback(async (q: string) => {
    setLoading(true)
    setError('')
    const id = ++reqIdRef.current
    try {
      const res = await fetch(`${API_URL}/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(q)}&limit=${LIMIT}&rating=pg`)
      if (!res.ok) throw new Error('Giphy error ' + res.status)
      const data = await res.json()
      if (id === reqIdRef.current) setGifs(data.data || [])
    } catch (e: unknown) {
      if (id === reqIdRef.current) setError('Falha ao buscar GIFs')
    } finally {
      if (id === reqIdRef.current) setLoading(false)
    }
  }, [])

  // Load trending on mount
  useEffect(() => {
    loadTrending()
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [loadTrending])

  // Debounced search
  const handleSearchChange = (e: ReactChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const val = e.target.value
    setQuery(val)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (!val.trim()) {
      loadTrending()
      return
    }
    searchTimerRef.current = setTimeout(() => searchGifs(val.trim()), 350)
  }

  const handleSelect = async (gif: { id: string; images?: { downsized?: { url?: string }; original?: { url?: string } }; url?: string }) => {
    if (fetching) return
    // Use downsized (smaller, still animated) for fast upload; fall back to original
    const img = gif.images?.downsized || gif.images?.original
    if (!img?.url) return
    setFetching(gif.id)
    try {
      const res = await fetch(img.url)
      if (!res.ok) throw new Error('download failed')
      const blob = await res.blob()
      const file = new File([blob], `giphy-${gif.id}.gif`, { type: 'image/gif' })
      onSelect(file)
    } catch {
      setError('Falha ao baixar GIF')
    } finally {
      setFetching(null)
    }
  }

  return (
    <div className="gif-picker" ref={ref}>
      <div className="gif-picker-search">
        <SearchIcon size={14} />
        <input
          type="text"
          placeholder="Buscar GIFs..."
          value={query}
          onChange={handleSearchChange}
          autoFocus
        />
      </div>
      {error && <div className="gif-picker-error">{error}</div>}
      <div className="gif-picker-grid">
        {loading && gifs.length === 0 && (
          <div className="gif-picker-empty"><LoaderIcon size={20} className="spin" /></div>
        )}
        {!loading && gifs.length === 0 && !error && (
          <div className="gif-picker-empty">Nenhum GIF encontrado</div>
        )}
        {gifs.map(gif => {
          const preview = gif.images?.fixed_height_small || gif.images?.fixed_height || gif.images?.original
          return (
            <button
              key={gif.id}
              type="button"
              className="gif-picker-item"
              onClick={() => handleSelect(gif)}
              disabled={!!fetching}
              title={gif.title || ''}
            >
              <img
                src={preview?.url}
                alt={gif.title || ''}
                loading="lazy"
              />
              {fetching === gif.id && (
                <span className="gif-picker-item-loading"><LoaderIcon size={16} className="spin" /></span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
